// web/src/components/rtls/AddAccessPointDialog.tsx
//
// Modal dialog for adding OR editing an access point. Mirrors
// AddLocationDialog: add mode pre-fills x/y/z from the last map tap;
// edit mode prefills all fields from the row in Redux. Submit → optimistic
// dispatch + Supabase INSERT or UPDATE, with rollback on error.

import React, { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '../../app/useAppDispatch'
import { useAppSelector } from '../../app/useAppSelector'
import { rtlsActions } from '@shared/slices/rtlsSlice'
import { uiActions } from '@shared/slices/uiSlice'
import { supabase } from '../../app/supabaseClient'
import type { AccessPoint, AccessPointStatus } from '@shared/types/rtlsTypes'
import { showToast } from '../Toast'

const STATUS_OPTIONS: AccessPointStatus[] = ['active', 'offline', 'fault']

interface AddAccessPointDialogProps {
  open: boolean
  onClose: () => void
  // When set, the dialog operates in edit mode: prefill from the existing
  // row and submit triggers UPDATE instead of INSERT.
  editingId?: string | null
}

export const AddAccessPointDialog: React.FC<AddAccessPointDialogProps> = ({
  open,
  onClose,
  editingId = null,
}) => {
  const dispatch = useAppDispatch()
  const tappedCoord        = useAppSelector(s => s.ui.tappedCoord)
  const mineId             = useAppSelector(s => s.map.mineId)
  const locationsRecord    = useAppSelector(s => s.rtls.locations)
  const accessPointsRecord = useAppSelector(s => s.rtls.accessPoints)

  const isEditing = !!editingId

  // Locations sorted by name for a stable dropdown order.
  const locationOptions = useMemo(
    () => Object.values(locationsRecord).sort((a, b) => a.name.localeCompare(b.name)),
    [locationsRecord],
  )

  const [name, setName]             = useState('')
  const [locationId, setLocationId] = useState('')
  const [x, setX]                   = useState('0')
  const [y, setY]                   = useState('0')
  const [z, setZ]                   = useState('0')
  const [level, setLevel]           = useState('')
  const [macAddress, setMacAddress] = useState('')
  const [status, setStatus]         = useState<AccessPointStatus>('active')
  const [submitting, setSubmitting] = useState(false)

  // Initial prefill on dialog open. Edit mode reads from Redux; add mode
  // uses the last map tap if present. Deliberately NOT reactive to
  // tappedCoord — mid-session taps handled separately below.
  useEffect(() => {
    if (!open) return
    if (editingId) {
      const ap = accessPointsRecord[editingId]
      if (ap) {
        setName(ap.name)
        setLocationId(ap.locationId ?? '')
        setX(ap.position.x.toFixed(2))
        setY(ap.position.y.toFixed(2))
        setZ(ap.position.z.toFixed(2))
        setLevel(ap.level != null ? String(ap.level) : '')
        setMacAddress(ap.macAddress ?? '')
        setStatus(ap.status)
      }
      return
    }
    if (tappedCoord) {
      setX(tappedCoord.x.toFixed(2))
      setY(tappedCoord.y.toFixed(2))
      setZ(tappedCoord.z.toFixed(2))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId])

  // Mid-session tap re-positions xyz (both modes).
  useEffect(() => {
    if (!open || !tappedCoord) return
    setX(tappedCoord.x.toFixed(2))
    setY(tappedCoord.y.toFixed(2))
    setZ(tappedCoord.z.toFixed(2))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tappedCoord])

  // Location is optional — no default selection. Empty string means "none".

  // Reset form on close.
  useEffect(() => {
    if (!open) {
      setName('')
      setLocationId('')
      setLevel('')
      setMacAddress('')
      setStatus('active')
      setSubmitting(false)
      dispatch(uiActions.setTappedCoord(null))
    }
  }, [open, dispatch])

  if (!open) return null

  const trimmedName = name.trim()
  const trimmedMac  = macAddress.trim()
  const xNum = parseFloat(x)
  const yNum = parseFloat(y)
  const zNum = parseFloat(z)
  const levelNum = level.trim() ? parseInt(level, 10) : undefined
  const canSubmit =
    !!trimmedName &&
    [xNum, yNum, zNum].every(Number.isFinite) &&
    !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)

    if (isEditing && editingId) {
      // ── Edit mode — UPDATE with optimistic dispatch + rollback ────────
      const prev = accessPointsRecord[editingId]
      if (!prev) {
        showToast('Cannot edit: access point no longer exists.', 'error')
        onClose()
        return
      }
      const updated: AccessPoint = {
        ...prev,
        locationId: locationId || undefined,
        name: trimmedName,
        position: { x: xNum, y: yNum, z: zNum },
        level: levelNum,
        status,
        macAddress: trimmedMac || undefined,
      }
      dispatch(rtlsActions.updateAccessPoint(updated))
      onClose()

      const { error } = await supabase
        .from('access_points')
        .update({
          location_id: locationId || null,
          name: trimmedName,
          x: xNum, y: yNum, z: zNum,
          level: levelNum ?? null,
          status,
          mac_address: trimmedMac || null,
        })
        .eq('id', editingId)
      if (error) {
        dispatch(rtlsActions.updateAccessPoint(prev))
        showToast(`Failed to save: ${error.message}`, 'error')
      } else {
        showToast('Access point updated.', 'success')
      }
      return
    }

    // ── Add mode — INSERT with optimistic dispatch + rollback ───────────
    // AP-prefixed ID matching the LOC-xxx / EQ-xxx convention.
    const id = `AP-${crypto.randomUUID().slice(0, 8)}`
    const newAp: AccessPoint = {
      id,
      locationId: locationId || undefined,
      name: trimmedName,
      position: { x: xNum, y: yNum, z: zNum },
      level: levelNum,
      status,
      macAddress: trimmedMac || undefined,
      mine_id: mineId ?? undefined,
      createdAt: Date.now(),
    }

    dispatch(rtlsActions.addAccessPoint(newAp))
    onClose()

    if (!mineId) {
      dispatch(rtlsActions.removeAccessPoint(id))
      showToast('Cannot save: no mine loaded. Check Supabase bootstrap.', 'error')
      return
    }

    const { error } = await supabase.from('access_points').insert({
      id,
      mine_id: mineId,
      location_id: locationId || null,
      name: trimmedName,
      x: xNum, y: yNum, z: zNum,
      level: levelNum ?? null,
      status,
      mac_address: trimmedMac || null,
      // radius_m uses DB default (50); unclickable in v1 since no coverage viz.
      // Note: access_points has no created_by column (locations does). The
      // copy-paste leftover from AddLocationDialog caused PGRST204 in prod.
    })
    if (error) {
      dispatch(rtlsActions.removeAccessPoint(id))
      showToast(`Failed to save access point: ${error.message}`, 'error')
    } else {
      showToast('Access point added.', 'success')
    }
  }

  const fieldCls =
    'w-full rounded-sm border border-surfaceBorder bg-surfaceCard px-sm py-xs text-sm text-textPrimary focus:border-primary focus:outline-none'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEditing ? 'Edit access point' : 'Add access point'}
      onClick={onClose}
    >
      <div
        className="w-96 rounded-md border border-surfaceBorder bg-surface p-lg shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="mb-md text-lg font-semibold text-textPrimary">
          {isEditing ? 'Edit access point' : 'Add access point'}
        </h2>

        <div className="flex flex-col gap-sm">
          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Name
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              className={fieldCls}
              placeholder="North Tunnel Router 1"
            />
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Parent location (optional)
            <select
              value={locationId}
              onChange={e => setLocationId(e.target.value)}
              className={fieldCls}
            >
              <option value="">— None —</option>
              {locationOptions.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-3 gap-sm">
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              X
              <input type="text" inputMode="decimal" value={x} onChange={e => setX(e.target.value)} className={fieldCls} />
            </label>
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              Y
              <input type="text" inputMode="decimal" value={y} onChange={e => setY(e.target.value)} className={fieldCls} />
            </label>
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              Z
              <input type="text" inputMode="decimal" value={z} onChange={e => setZ(e.target.value)} className={fieldCls} />
            </label>
          </div>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Level (optional)
            <input
              type="text"
              inputMode="numeric"
              value={level}
              onChange={e => setLevel(e.target.value)}
              className={fieldCls}
              placeholder="e.g. 2"
            />
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            MAC address (optional)
            <input
              type="text"
              value={macAddress}
              onChange={e => setMacAddress(e.target.value)}
              className={fieldCls}
              placeholder="00:1B:44:11:3A:B7"
            />
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Status
            <select
              value={status}
              onChange={e => setStatus(e.target.value as AccessPointStatus)}
              className={fieldCls}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          {tappedCoord && (
            <p className="text-xs text-textMuted">
              Tip: click a point on the 3D map to pre-fill X/Y/Z.
            </p>
          )}
        </div>

        <div className="mt-lg flex justify-end gap-sm">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm px-md py-xs text-sm text-textMuted hover:text-textPrimary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'rounded-sm px-md py-xs text-sm font-semibold transition-colors',
              canSubmit
                ? 'bg-primary text-black hover:bg-primaryDark'
                : 'cursor-not-allowed bg-surfaceCard text-textDisabled',
            )}
          >
            {submitting ? 'Saving...' : isEditing ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  )
}
